import Logout from '@mui/icons-material/Logout';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { type MouseEvent, type ReactNode, useState } from 'react';
import { InitialsAvatar } from '../InitialsAvatar';
import { SettingsIcon } from '../icons/SettingsIcon';
import type { Account } from './types';

interface AccountMenuProps {
  account: Account;
  onSettings?: (() => void) | undefined;
  onLogout: () => void;
}

export function AccountMenu({ account, onSettings, onLogout }: AccountMenuProps): ReactNode {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  return (
    <>
      <InitialsAvatar
        component={ButtonBase}
        variant="rounded"
        size={40}
        initials={account.initials}
        onClick={(event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget)}
        title="Your account"
        aria-label="Your account"
        sx={{
          borderRadius: 2,
          boxShadow: open ? (theme) => `0 0 0 3px ${theme.palette.primary.bg}` : 'none',
          transition: 'box-shadow .15s',
          '&:hover': {
            boxShadow: open ? (theme) => `0 0 0 3px ${theme.palette.primary.bg}` : 4,
          },
        }}
      />

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: { sx: { ml: 1, minWidth: 220, boxShadow: 5 } },
          list: { sx: { p: 1.5 } },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            px: 2,
            pt: 1,
            pb: 2,
            mb: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <InitialsAvatar size={38} initials={account.initials} sx={{ fontSize: 14 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary' }}>
              {account.name}
            </Typography>
            {(account.org || account.full) && (
              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                {[account.org, account.full].filter(Boolean).join(' · ')}
              </Typography>
            )}
          </Box>
        </Box>

        {onSettings ? (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              onSettings();
            }}
            sx={{ gap: 1.5, py: 1, px: 2 }}
          >
            <SettingsIcon sx={{ fontSize: 16 }} />
            Settings
          </MenuItem>
        ) : null}
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            onLogout();
          }}
          sx={{
            gap: 1.5,
            py: 1,
            px: 2,
            color: 'error.main',
            '&:hover': { bgcolor: 'error.bg' },
          }}
        >
          <Logout sx={{ fontSize: 16 }} />
          Log out
        </MenuItem>
      </Menu>
    </>
  );
}
